'use strict';

/**
 * Base class for a CompositeView
 *
 * @category   View
 * @module     base
 * @copyright  Copyright (c) 2014, COLIDO (http://www.colido.net)
 * @author     Stefan Rueschenberg <sr@humans.am>
 * @author     Jonas Girnatis <dermusterknabe@gmail.com>
 */

var Marionette = require('backbone-marionette');
var Model = require('base/Colido.Model');
var sortViewExtension = require('base/mixins/sort-view');

var CompositeView = Marionette.CompositeView.extend({
    /**
     * Activate deactivate lazy render behaviour
     * @type {boolean}
     */
    lazyRender: false,

    /**
     * Scroll distance from top in pixels when previous page is rendered.
     * @type {integer}
     */
    lazyRenderPreviousPageThreshold: 0,

    /**
     * Number of items rendered per page
     * @type {integer}
     */
    lazyRenderItemsPerPage: 20,

    /**
     * Currenty rendered max page
     * @type {integer}
     */
    lazyRenderPage: 0,

    /**
     * Element scroll handler is attached to, defaults to view's own element
     * @type {jQuery element}
     */
    lazyRenderScrollEl: null,

    /**
     * Limits the output to x items
     * @type {number}
     */
    itemLimit: null,

    /**
     * Offset for the item limitation
     * @type {number}
     */
    itemLimitOffset: null,

    constructor: function() {
        Marionette.CompositeView.prototype.constructor.apply(this, arguments);

        this.listenTo(App.vent, 'resizeThrottled', function() { this.triggerMethod('resize'); }.bind(this));
    },

    initialize: function(options) {
        options = _.extend({}, options);
        _.extend(this.viewData, options.viewData);

        this.lazyRender = this.getOption('lazyRender');
        this.lazyRenderPreviousPageThreshold = this.getOption('lazyRenderPreviousPageThreshold');
        this.lazyRenderItemsPerPage = this.getOption('lazyRenderItemsPerPage');
        this.lazyRenderScrollEl = this.getOption('lazyRenderScrollEl') || this.$el;

        this.applyLazyRender = this.applyLazyRender.bind(this);

        // Create proper context
        this.onScrollLazyRender = this.onScrollLazyRender.bind(this);
        this.appVentScrollEvent = this.appVentScrollEvent.bind(this);

        this.onAttach = this.onAttach.bind(this);

        this.listenToWindowResize();

        this.on('before:render:collection', this.applyLazyRender);

        if (browser.isIos) {
            this.maintainViewportPosition = _.debounce(this.maintainViewportPosition.bind(this), 100);
        }

        this.itemLimit = this.getOption('itemLimit') || 0;

        // Apply sort if sort extension is available
        if (_.isString(this.getOption('sortDefault'))
            && this.getOption('sortDefault').length > 0
            && _.isFunction(this.sortByFields)
        ) {
            this.sortByFields(this.getOption('sortDefault'));
        }
    },

    /**
     * Activates lazy render behaviour. Resets current page and attaches
     * scroll handler.
     */
    applyLazyRender: function() {
        if (!this.lazyRender) {
            return;
        }

        this.stopListeningToScroll();

        var page = 0;
        var activeModel = this.model ? this.model.get('activeModel') : this.getOption('activeChildModel');
        if (activeModel) {
            var activeIndex = this.collection.indexOf(activeModel);
            page = Math.floor(activeIndex / this.lazyRenderItemsPerPage);
        }
        this.lazyRenderPage = page;

        this.listenToScroll();
    },

    appVentScrollEvent: function(event)  {
        App.vent.trigger('scrollContainerScroll', event);
    },

    listenToScroll: function () {
        if (!this.lazyRenderScrollEl.data('on-scroll-lazy-render')) {
            this.lazyRenderScrollEl.on('scroll', this.onScrollLazyRender);
            this.lazyRenderScrollEl.data('on-scroll-lazy-render', true);
        }

        if (!this.lazyRenderScrollEl.data('app-vent-scroll-event')) {
            this.lazyRenderScrollEl.on('scroll', this.appVentScrollEvent);
            this.lazyRenderScrollEl.data('app-vent-scroll-event', true);
        }
    },

    /**
     * Deactivates lazy render behaviour by removing scroll handler
     */
    stopListeningToScroll: function () {
        if (this.lazyRenderScrollEl) {
            if (this.lazyRenderScrollEl.data('on-scroll-lazy-render')) {
                this.lazyRenderScrollEl.off('scroll', this.onScrollLazyRender);
                this.lazyRenderScrollEl.removeData('on-scroll-lazy-render');
            }

            if (this.lazyRenderScrollEl.data('app-vent-scroll-event')) {
                this.lazyRenderScrollEl.off('scroll', this.appVentScrollEvent);
                this.lazyRenderScrollEl.removeData('app-vent-scroll-event');
            }
        }
    },

    /**
     * Scroll handler for lazy render behaviour. When the user has scrolled to
     * the bottom of the element, add another bunch of items.
     */
    onScrollLazyRender: function() {
        if (this.collection.length === 0) {
            return;
        }

        var visibleHeight = this.lazyRenderScrollEl.outerHeight();
        if (!visibleHeight) {
            // Not attached to DOM yet, nothing to do here.
            return;
        }

        var children = this.getChildrenEl();
        if (!children.length) {
            return;
        }

        this.stopListeningToScroll();

        // Use threshold bases on rem size
        var threshold = _.isString(this.lazyRenderPreviousPageThreshold)
                      ? this.getPxSize(parseInt(this.lazyRenderPreviousPageThreshold))
                      : this.lazyRenderPreviousPageThreshold;

        // use position of last element as threshold
        var lastEl = children.last();
        if (lastEl.offset().top - this.lazyRenderScrollEl.offset().top <= visibleHeight) {
            // scrolled down to bottom, show next page
            this.maintainViewportPosition(children.eq(this.lazyRenderItemsPerPage), this.renderNextPage);
        } else if (this.lazyRenderPage > 1 && this.lazyRenderScrollEl.scrollTop() <= threshold) {
            // scrolled down to top, show previous page
            this.maintainViewportPosition(children.first(), this.renderPreviousPage);
        }

        this.listenToScroll();
    },

    /**
     * Performs action and afterwards sets scroll position so that a pivot
     * element maintains its vertical position in the viewport.
     */
    maintainViewportPosition: function (el, action) {
        if (el.length) {
            var scrollEl = this.lazyRenderScrollEl;

            var previousTop = el.offset().top;

            action.call(this);

            var newTop = el.offset().top;
            var diff = newTop - previousTop;

            scrollEl.scrollTop(scrollEl.scrollTop() + diff);
        } else {
            action.call(this);
        }
    },

    /**
     * Renders the next 'page' during lazy rendering.
     */
    renderNextPage: function() {
        var maxPage = Math.floor(this.collection.length / this.lazyRenderItemsPerPage);
        this.lazyRenderPage = Math.min(maxPage, this.lazyRenderPage + 1);
        this.showCollection();
    },

    /**
     * Renders the previous 'page' during lazy rendering.
     */
    renderPreviousPage: function() {
        this.lazyRenderPage = Math.max(0, this.lazyRenderPage - 1);
        this.showCollection();
    },

    onBeforeRender: function () {
        this.applyLazyRender();
    },

    onRender: function () {
        if (this.lazyRender) {
            this.stopListeningToScroll();
            this.scrollToFirstVisibleElement();
            this.listenToScroll();
        }
    },

    /**
     * Sets scroll position to current lazy render page.
     * Ensures enough elements are rendered that a scrollbar is visible.
     */
    onAttach: function() {
        if (!this.lazyRender) {
            return;
        }

        this.stopListeningToScroll();

        var numVisible = this.visibleChildren();
        var itemsPerPage = this.lazyRenderItemsPerPage;

        // Ensure that we render more items than fits in the viewport
        if (numVisible * 1.5 > this.lazyRenderItemsPerPage) {
            itemsPerPage = numVisible * 1.5;
        }

        // Ensure that we only add and remove full rows.
        var childrenPerRow = this.childrenPerRow();
        if (itemsPerPage % childrenPerRow !== 0) {
            itemsPerPage += childrenPerRow - itemsPerPage % childrenPerRow;
        }

        if (itemsPerPage !== this.lazyRenderItemsPerPage) {
            this.lazyRenderItemsPerPage = itemsPerPage;
            this.getChildrenEl().remove();
            this.render();
            this.onAttach();

            return;
        }

        this.listenToScroll();
    },

    /**
     * Checks whether this view is attached to the DOM
     */
    isAttached: function () {
        return Boolean(this.$el.closest($('body')).length);
    },

    /**
     * Scrolls to element that should be visible in top left corner of viewport.
     */
    scrollToFirstVisibleElement: function () {
        if (!this.isAttached() || this.collection.length === 0) {
            return;
        }

        this.stopListeningToScroll();

        var scrollEl = this.lazyRenderScrollEl;
        var firstVisibleEl = this.getChildrenEl().eq(this.lazyRenderItemsPerPage);
        if (this.lazyRenderPage > 0) {
            if (firstVisibleEl.length) {
                scrollEl.scrollTop(firstVisibleEl.position().top);
            }
        } else {
            scrollEl.scrollTop(0);
        }

        this.listenToScroll();
    },

    /**
     * Attaches listener that resets lazy rendering on window resize.
     */
    listenToWindowResize: function () {
        this.debouncedWindowResize = _.debounce(this.onAttach.bind(this), 300);
        $(window).on('resize', this.debouncedWindowResize);
    },

    onDestroy: function () {
        this.stopListeningToScroll();
        $(window).off('resize', this.debouncedWindowResize);
    },

    /**
     * Counts the number of children per row.
     */
    childrenPerRow: function () {
        var children = this.getChildrenEl();
        if (!children.length) {
            return 0;
        }

        var top = $(children[0]).offset().top;
        var count = 1;
        for (var i = 1; i < children.length; i++) {
            if (top === $(children[i]).offset().top) {
                count++;
            } else {
                break;
            }
        }

        return count;
    },

    /**
     * Counts the maximum number of children visible at any one time.
     */
    visibleChildren: function () {
        // Number of children per viewport
        var scrollEl = this.lazyRenderScrollEl;
        var scrollElTop = scrollEl.offset().top;
        var visibleHeight = scrollEl.outerHeight();
        var numVisible = _.reduce(this.getChildrenEl(), function (count, el) {
            var top = $(el).offset().top - scrollElTop;
            if (top >= 0 && top < visibleHeight) {
                count++;
            }

            return count;
        }, 0);

        // Add another row to account for half-visible rows, in which case the
        // first row is cut off at the top and an additional row is half-visible
        // at the bottom.
        numVisible += this.childrenPerRow();

        return numVisible;
    },

    /**
     * Retrieves jQuery references to child elements
     */
    getChildrenEl: function() {
        return this.getChildViewContainer(this).children();
    },

    /**
     * Overrides internal method of Marionette.CollectionView to ensure elements
     * added by lazy rendering are added at the correct position
     */
    showCollection: function() {
        if (!this.lazyRender && !(this.itemLimit || this.itemLimitOffset)) {
            Marionette.CompositeView.prototype.showCollection.apply(this, arguments);

            return;
        }

        var models = this._filteredSortedModels();
        var modelsByCid = _.object(_.map(models, function (model) {
            return [model.cid, {model: model, exists: false}];
        }));

        this.children.each(function (child) {
            if (modelsByCid[child.model.cid]) {
                // model in selection, view already exists
                modelsByCid[child.model.cid].exists = true;
            } else {
                // model not in selection anymore, remove child view
                this.removeChildView(child);
            }
        }.bind(this));

        var ChildView;
        _.each(models, function(model) {
            if (!modelsByCid[model.cid].exists) {
                ChildView = this.getChildView(model);
                var index = _.indexOf(models, model);
                this.addChild(model, ChildView, index);
            }
        }, this);
    },

    /**
     * Overrides internal method of Marionette.CollectionView to return only
     * the items for the current page when lazy render is active or items should be limited.
     */
    _filteredSortedModels: function() {
        var models = Marionette.CompositeView.prototype._filteredSortedModels.apply(this);

        if (this.lazyRender) {
            var page = this.lazyRenderPage || 0;
            var firstIndex = Math.max(0, page - 1) * this.lazyRenderItemsPerPage;
            var numItems = this.lazyRenderItemsPerPage;
            if (page > 0) {
                numItems += this.lazyRenderItemsPerPage;
            }

            if (_.result(this, 'showVirtualItem')) {
                if (page <= 1) {
                    numItems--;
                } else {
                    firstIndex--;
                }
                models = models.slice(firstIndex, firstIndex + numItems);
                if (page <= 1) {
                    models.unshift(new Model({virtualItem: true}));
                }
            } else {
                models = models.slice(firstIndex, firstIndex + numItems);
            }
        } else {
            if (_.result(this, 'showVirtualItem')) {
                models.unshift(new Model({virtualItem: true}));
            }
        }

        // Should items be limited?
        if (this.itemLimit && this.itemLimit > 0 && this.itemLimit <= models.length) {
            models = _.part(models, this.itemLimit, this.itemLimitOffset);
        }

        return models;
    },

    /**
     * Sets a new item limitation
     * @param {number}   limit  The limit to set
     * @param {number}   offset The offset to set
     * @param {boolean=} render (Optional) Render collection, based on new settings?
     */
    setItemLimit: function(limit, offset, render) {
        this.itemLimit = limit;
        this.itemLimitOffset = offset;

        if (render) {
            this.showCollection();
        }
    },

    /**
     * Resets the itemlimit
     * @param {boolean=} render (Optional) Render collection, based on new settings?
     */
    resetItemLimit: function(render) {
        this.setItemLimit(null, null, render);
    },

    showVirtualItem: false,

    /**
     * Always force resort in lazy render mode when 'sort' event is triggered.
     * Marionette's way of determining if sort order has changed is not
     * compatible with lazy render.
     */
    _sortViews: function () {
        if (this.lazyRender) {
            this.resortView();
        } else {
            Marionette.CompositeView.prototype._sortViews.apply(this, arguments);
        }
    },
});

// Apply Mixins
_.extend(CompositeView.prototype, sortViewExtension);

module.exports = CompositeView;